import { Button } from '@crowdfpl/ui-web/button';
import { Dialog, DialogContent } from '@crowdfpl/ui-web/dialog';
import { useQueryClient } from '@tanstack/react-query';
import { Flag } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { CountrySelector } from '@/components/shared/CountrySelector';
import { MY_PROFILE_QUERY_KEY } from '@/constants';
import { trackUmamiEvent } from '@/helpers/analytics';
import { getCountryName } from '@/helpers/countries';
import { useMyProfile, useUpdateProfileSettings } from '@/hooks/data/profiles';
import { notify } from '@/services/notifications';

interface CountryPromptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Delay before refetching /me when modal opened without request_country (backend may set it async). */
const REFETCH_ME_DELAY_MS = 800;

export function CountryPromptModal({ open, onOpenChange }: CountryPromptModalProps) {
  const [country, setCountry] = useState<string | null>(null);
  const { data: myProfile } = useMyProfile();
  const updateSettings = useUpdateProfileSettings({ silent: true });
  const queryClient = useQueryClient();
  const didRefetchForRequestCountry = useRef(false);

  // Sync country from profile when modal is open (including when request_country appears after refetch).
  useEffect(() => {
    if (open && myProfile) {
      setCountry((prev) => myProfile.country ?? myProfile.request_country ?? prev ?? null);
    }
  }, [open, myProfile]);

  // When modal opens with no country and no request_country, refetch /me once so we pick up
  // request_country when backend returns it (avoids empty dropdown until user leaves/returns to tab).
  useEffect(() => {
    if (!open) {
      didRefetchForRequestCountry.current = false;
      return;
    }
    if (!myProfile) return;
    const hasCountry = myProfile.country ?? myProfile.request_country;
    if (hasCountry) return;
    if (didRefetchForRequestCountry.current) return;

    const t = setTimeout(() => {
      didRefetchForRequestCountry.current = true;
      queryClient.refetchQueries({ queryKey: MY_PROFILE_QUERY_KEY });
    }, REFETCH_ME_DELAY_MS);
    return () => clearTimeout(t);
  }, [open, myProfile, queryClient]);

  const handleSave = async () => {
    if (!country) return;
    try {
      await updateSettings.mutateAsync({ country });
      trackUmamiEvent('country_modal_save_click', { country });
      notify.success(`Joined ${getCountryName(country)} League`);
      onOpenChange(false);
    } catch {
      // Error handled by mutation
    }
  };

  const handleMaybeLater = () => {
    trackUmamiEvent('country_modal_maybe_later_click');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-md bg-background border-border rounded-2xl p-8">
        <div className="flex flex-col items-center text-center space-y-6">
          <div className="h-14 w-14 rounded-full bg-foreground/10 flex items-center justify-center">
            <Flag className="h-7 w-7 text-foreground" />
          </div>

          <h2 className="text-xl font-bold text-foreground">Add your country</h2>

          <div className="space-y-2 w-full text-left">
            <p className="text-sm text-foreground">
              Let other managers spot you on the leaderboard and see who's top in your region.
            </p>
            <CountrySelector
              value={country}
              onChange={setCountry}
              placeholder="Select country"
              className="w-full"
            />
          </div>

          <div className="w-full space-y-3 pt-2">
            <Button
              onClick={handleSave}
              disabled={!country || updateSettings.isPending}
              className="w-full h-11"
            >
              Save
            </Button>
            <button
              type="button"
              onClick={handleMaybeLater}
              className="w-full text-xs text-muted-foreground hover:underline"
            >
              Maybe later
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
